import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../utils/api.js";
import { URLS, ImgURL } from "../utils/urls.js";
import AdminMediaGallery from "./AdminMediaGallery.jsx";
import { useTopLoader } from "../context/TopLoaderContext";
import Alert from "../components/Alert.jsx";

const PersonalityEdit = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const { start, stop } = useTopLoader();

  // Alert State
  const [show, setShow] = useState(false);
  const [alertText, setAlertText] = useState("");
  const [alertType, setAlertType] = useState("success");

  // State to handle the two input fields for the playlist
  const [vibeInput, setVibeInput] = useState({ title: "", song: "" });

  const [personality, setPersonality] = useState({
    title: "",
    code: "",
    description: "",
    who_you_are: "",
    friendship_tip: "",
    playlist_vibe: "[]", // Default to empty JSON array string
    playlist_url: "",
    image_url: "",
    image_thumb_150: "",
    image_thumb_720: "",
    icon_url: "",
    icon_thumb_150: "",
    cover_image_url: "",
    cover_image_thumb_150: "",
    cover_image_thumb_720: "",
  });
  const [galleryTarget, setGalleryTarget] = useState("image_url"); // "image_url", "icon_url", or "cover_image_url"

  const ALERT = (msg, type = "success") => {
    setAlertText(msg);
    setAlertType(type);
    setShow(true);
  };

  const [associatedSongs, setAssociatedSongs] = useState([]);

  const loadPersonality = async () => {
    start();
    try {
      const res = await api.get(URLS.GET_PERSONALITY(id));
      const data = res.data;
      if (!data.playlist_vibe) data.playlist_vibe = "[]";
      
      // Ensure specific fields are explicitly set if they came back in snake_case
      setPersonality({
          ...data,
          cover_image_url: data.cover_image_url || "",
          cover_image_thumb_150: data.cover_image_thumb_150 || "",
          cover_image_thumb_720: data.cover_image_thumb_720 || ""
      });

      // Fetch all songs and filter for this personality
      if (data.code) {
        const songsRes = await api.get(URLS.GET_SONG_LIST);
        const filtered = songsRes.data.filter(s => s.personality_codes?.includes(data.code));
        setAssociatedSongs(filtered);
      }
    } catch (err) {
      console.error("Unable to load personality", err);
      ALERT("Unable to load personality", "error");
    } finally {
      setLoading(false);
      stop();
    }
  };

  useEffect(() => {
    loadPersonality();
  }, [id]);

  const updateField = (key, value) => {
    setPersonality(prev => ({ ...prev, [key]: value }));
  };

  const openGallery = (target) => {
    setGalleryTarget(target);
    setShowGallery(true);
  };

  // Robustly parse vibesList from personality.playlist_vibe
  const getVibesList = () => {
    const raw = personality.playlist_vibe || "[]";
    try {
      // If it looks like JSON array, parse it
      if (raw.trim().startsWith("[")) {
        return JSON.parse(raw);
      }
      // Otherwise treat as plain text and wrap in array
      return [raw];
    } catch (e) {
      console.warn("Failed to parse playlist_vibe, treating as plain text", e);
      return [raw];
    }
  };

  const vibesList = getVibesList();

  const handleAddVibe = () => {
    if (!vibeInput.title || !vibeInput.song) return;
    const newVibe = `${vibeInput.title} – ${vibeInput.song}`;
    const newList = [...vibesList, newVibe];
    updateField("playlist_vibe", JSON.stringify(newList));
    setVibeInput({ title: "", song: "" });
  };

  const handleRemoveVibe = (index) => {
    const newList = vibesList.filter((_, i) => i !== index);
    updateField("playlist_vibe", JSON.stringify(newList));
  };

  const savePersonality = async () => {
    start();
    setSaving(true);
    try {
      await api.patch(URLS.UPDATE_PERSONALITY(id), personality);
      ALERT("Personality updated successfully!");
    } catch (err) {
      console.error("Save failed", err);
      ALERT("Failed to update personality", "error");
    } finally {
      setSaving(false);
      stop();
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-gray-500">Loading...</div>;
  }

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      <h1 className="text-3xl font-semibold mb-6">Edit Personality</h1>

      {/* FORM */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-white p-6 border rounded-lg shadow">

        <div>
          <label className="font-semibold">Title</label>
          <input
            className="w-full border p-2 rounded mt-1"
            value={personality.title}
            onChange={(e) => updateField("title", e.target.value)}
          />
        </div>

        <div>
          <label className="font-semibold text-orange-600">Code (3-letter, e.g. NUR)</label>
          <input
            className="w-full border-orange-200 border p-2 rounded mt-1 text-orange-800"
            maxLength={10}
            value={personality.code || ""}
            onChange={(e) => updateField("code", e.target.value.toUpperCase())}
          />
        </div>

        <div>
          <label className="font-semibold text-blue-600">YouTube Playlist URL</label>
          <input
            className="w-full border-blue-200 border p-2 rounded mt-1 text-blue-800"
            placeholder="https://youtube.com/playlist?list=..."
            value={personality.playlist_url || ""}
            onChange={(e) => updateField("playlist_url", e.target.value)}
          />
        </div>

        <div className="flex gap-6">
            {/* IMAGE PICKER */}
            <div className="flex-1">
                <label className="font-semibold block mb-2">Profile Image</label>

                {personality.image_url ? (
                    <img
                    src={ImgURL(personality.image_thumb_720 || personality.image_url)}
                    alt="Personality"
                    className="w-32 h-32 object-cover rounded border mb-2"
                    />
                ) : (
                    <div className="w-32 h-32 bg-gray-100 rounded border mb-2 flex items-center justify-center text-gray-400 text-xs text-center p-2">
                        No Image Selected
                    </div>
                )}

                <button
                    onClick={() => openGallery("image_url")}
                    className="w-full py-1 bg-gray-800 text-white text-sm rounded hover:bg-gray-900"
                >
                    Change Image
                </button>
            </div>

            {/* COVER IMAGE PICKER */}
            <div className="flex-1">
                <label className="font-semibold block mb-2 text-green-700">Cover Image (Lobby Background)</label>

                {personality.cover_image_url ? (
                    <img
                    src={ImgURL(personality.cover_image_thumb_720 || personality.cover_image_url)}
                    alt="Cover"
                    className="w-full h-32 object-cover rounded border mb-2"
                    />
                ) : (
                    <div className="w-full h-32 bg-gray-100 rounded border mb-2 flex items-center justify-center text-gray-400 text-xs text-center p-2">
                        No Cover Selected
                    </div>
                )}

                <button
                    onClick={() => openGallery("cover_image_url")}
                    className="w-full py-1 bg-green-800 text-white text-sm rounded hover:bg-green-900"
                >
                    Change Cover
                </button>
            </div>
        </div>
        <div className="col-span-2">
          <label className="font-semibold">Description</label>
          <textarea
            className="w-full border p-2 rounded mt-1"
            rows={3}
            value={personality.description}
            onChange={(e) => updateField("description", e.target.value)}
          />
        </div>

        <div className="col-span-2">
          <label className="font-semibold">Who You Are</label>
          <textarea
            className="w-full border p-2 rounded mt-1"
            rows={4}
            value={personality.who_you_are}
            onChange={(e) => updateField("who_you_are", e.target.value)}
          />
        </div>

        <div className="col-span-2">
          <label className="font-semibold">Friendship Tip</label>
          <textarea
            className="w-full border p-2 rounded mt-1"
            rows={3}
            value={personality.friendship_tip}
            onChange={(e) => updateField("friendship_tip", e.target.value)}
          />
        </div>

        {/* --- NEW PLAYLIST VIBE BUILDER --- */}
        <div className="col-span-2 border p-4 rounded bg-gray-50">
          <label className="font-semibold block mb-2">Curated Songs (from Database)</label>
          <p className="text-sm text-gray-500 mb-3">These songs are associated with this personality in the Song Manager.</p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            {associatedSongs.length > 0 ? associatedSongs.map(song => (
              <div key={song.id} className="flex items-center gap-3 bg-white p-2 border rounded shadow-sm">
                <div className="w-10 h-10 bg-orange-100 rounded flex items-center justify-center text-orange-600 font-bold">
                    {song.platform[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{song.title}</p>
                    <p className="text-xs text-gray-500 truncate">{song.artist}</p>
                </div>
                <a href={song.song_url} target="_blank" rel="noreferrer" className="text-blue-500 hover:text-blue-700 p-1">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                    <path d="M6.271 5.055a.5.5 0 0 1 .52.038l3.5 2.5a.5.5 0 0 1 0 .814l-3.5 2.5A.5.5 0 0 1 6 10.5v-5a.5.5 0 0 1 .271-.445z"/>
                  </svg>
                </a>
              </div>
            )) : (
              <p className="text-sm text-gray-400 italic">No curated songs associated yet.</p>
            )}
          </div>

          <label className="font-semibold block mb-2 mt-6">Manual Playlist Text (Fallback)</label>
          <p className="text-sm text-gray-500 mb-3">Add text-only items if you don't want to add them as full songs.</p>

          {/* List of existing vibes */}
          {vibesList.length > 0 && (
            <ul className="mb-4 space-y-2">
              {vibesList.map((vibe, index) => (
                <li key={index} className="flex items-center justify-between bg-white p-2 border rounded">
                  <span className="text-sm">{vibe}</span>
                  <button
                    onClick={() => handleRemoveVibe(index)}
                    className="text-red-500 hover:text-red-700 px-2 font-bold"
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* Inputs to add new vibe */}
          <div className="flex flex-col md:flex-row gap-2">
            <input
              className="border p-2 rounded flex-1"
              placeholder="Artist / Title (e.g. Burna Boy)"
              value={vibeInput.title}
              onChange={(e) => setVibeInput({ ...vibeInput, title: e.target.value })}
            />
            <input
              className="border p-2 rounded flex-1"
              placeholder="Song Name (e.g. Ye)"
              value={vibeInput.song}
              onChange={(e) => setVibeInput({ ...vibeInput, song: e.target.value })}
            />
            <button
              onClick={handleAddVibe}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
            >
              Add
            </button>
          </div>
        </div>
        {/* --- END PLAYLIST VIBE BUILDER --- */}

        <button
          onClick={savePersonality}
          disabled={saving}
          className="col-span-2 mt-4 px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          {saving ? "Saving..." : "Save Personality"}
        </button>
      </div>

      {/* MEDIA GALLERY MODAL */}
      {showGallery && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white w-11/12 max-w-5xl p-4 rounded-lg shadow-lg relative">
            <button
              className="absolute top-3 right-3 text-gray-600 hover:text-black"
              onClick={() => setShowGallery(false)}
            >
              ✕
            </button>

            <AdminMediaGallery
              accept="image/*"
              onSelect={(m) => {
                if (galleryTarget === "image_url") {
                  setPersonality(prev => ({
                    ...prev,
                    image_url: m.url,
                    image_thumb_150: m.thumb_150,
                    image_thumb_720: m.thumb_720
                  }));
                } else if (galleryTarget === "icon_url") {
                  setPersonality(prev => ({
                    ...prev,
                    icon_url: m.url,
                    icon_thumb_150: m.thumb_150
                  }));
                } else if (galleryTarget === "cover_image_url") {
                    setPersonality(prev => ({
                      ...prev,
                      cover_image_url: m.url,
                      cover_image_thumb_150: m.thumb_150,
                      cover_image_thumb_720: m.thumb_720
                    }));
                }
                setShowGallery(false);
              }}
            />
          </div>
        </div>
      )}
       {show && (
              <Alert
                message={alertText}
                type={alertType}
                onClose={() => setShow(false)}
              />
            )}
    </div>
  );
};

export default PersonalityEdit;